import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking, MOCK_DB } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMock(element, assertions, waitMs = 400) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await act(async () => { await new Promise(r => setTimeout(r, 100)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

export const meetingAnalysisTests = [

  // ─── Meetings page ──────────────────────────────────────────────────────────

  {
    id: 'meetings-page-renders',
    name: 'Meetings page: renders without crashing',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMock(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="meetings-page"]') !== null, 'meetings-page container must render');
        }
      );
    },
  },

  {
    id: 'meetings-page-grid',
    name: 'Meetings page: renders grid of meeting cards from API',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMock(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          const grid = container.querySelector('[data-testid="meetings-grid"]');
          assert(grid !== null, 'meetings-grid must render after data loads');
        }
      );
    },
  },

  {
    id: 'meetings-page-card-count',
    name: 'Meetings page: shows one card per meeting returned by API',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMock(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          const cards = container.querySelectorAll('[data-testid^="meeting-card-"]');
          assert(
            cards.length === MOCK_DB.meetings.length,
            `Expected ${MOCK_DB.meetings.length} meeting cards, found ${cards.length}`
          );
        }
      );
    },
  },

  {
    id: 'meetings-page-card-names',
    name: 'Meetings page: each meeting card shows the meeting name',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMock(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          MOCK_DB.meetings.forEach(m => {
            const card = container.querySelector(`[data-testid="meeting-card-${m.uuid}"]`);
            assert(card !== null, `Card for meeting "${m.name}" (${m.uuid}) must exist`);
            assert(card.textContent.includes(m.name), `Card must show meeting name "${m.name}"`);
          });
        }
      );
    },
  },

  {
    id: 'meetings-page-processed-status',
    name: 'Meetings page: processed meetings show "View More" action',
    run: async () => {
      const Meetings = require('../../Pages/Private/Settings/Meetings/Meetings').default;
      return await renderWithMock(
        withWrapper(<Meetings />, '/meetings'),
        async ({ container }) => {
          const processedMeeting = MOCK_DB.meetings.find(m => m.status === 'Processed');
          if (!processedMeeting) return;
          const card = container.querySelector(`[data-testid="meeting-card-${processedMeeting.uuid}"]`);
          assert(card !== null, `Processed meeting card must exist`);
          assert(card.textContent.includes(processedMeeting.text_summary), 'Card must show text summary');
        }
      );
    },
  },

  // ─── Analysis page ──────────────────────────────────────────────────────────

  {
    id: 'analysis-page-renders',
    name: 'Analysis page: renders without crashing',
    run: async () => {
      const Analysis = require('../../Pages/Private/Settings/Analysis/Analysis').default;
      return await renderWithMock(
        withWrapper(<Analysis />, '/analysis'),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="analysis-page"]') !== null, 'analysis-page container must render');
        }
      );
    },
  },

  {
    id: 'analysis-page-grid',
    name: 'Analysis page: shows analysis grid after API loads',
    run: async () => {
      const Analysis = require('../../Pages/Private/Settings/Analysis/Analysis').default;
      return await renderWithMock(
        withWrapper(<Analysis />, '/analysis'),
        async ({ container }) => {
          const grid = container.querySelector('[data-testid="analysis-grid"]');
          assert(grid !== null, 'analysis-grid must render when analysis data is loaded');
        }
      );
    },
  },

  {
    id: 'analysis-page-card-count',
    name: 'Analysis page: shows one card per analysis returned by API',
    run: async () => {
      const Analysis = require('../../Pages/Private/Settings/Analysis/Analysis').default;
      return await renderWithMock(
        withWrapper(<Analysis />, '/analysis'),
        async ({ container }) => {
          const cards = container.querySelectorAll('[data-testid^="analysis-card-"]');
          assert(
            cards.length === MOCK_DB.analysis.length,
            `Expected ${MOCK_DB.analysis.length} analysis cards, found ${cards.length}`
          );
        }
      );
    },
  },

  {
    id: 'analysis-page-card-names',
    name: 'Analysis page: each analysis card shows the analysis name',
    run: async () => {
      const Analysis = require('../../Pages/Private/Settings/Analysis/Analysis').default;
      return await renderWithMock(
        withWrapper(<Analysis />, '/analysis'),
        async ({ container }) => {
          MOCK_DB.analysis.forEach(a => {
            const card = container.querySelector(`[data-testid="analysis-card-${a.uuid}"]`);
            assert(card !== null, `Analysis card for "${a.name}" (${a.uuid}) must exist`);
            assert(card.textContent.includes(a.name), `Card must show analysis name "${a.name}"`);
          });
        }
      );
    },
  },

  // ─── File Upload type selector ───────────────────────────────────────────────

  {
    id: 'upload-types-renders',
    name: 'UploadFileWithTypes: renders all 4 type buttons and cancel',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      return await renderWithMock(
        withWrapper(<UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={() => {}} />, '/analysis'),
        async () => {
          // headlessui Dialog renders into a portal on document.body, not inside the container
          assert(document.body.querySelector('[data-testid="upload-type-audio"]') !== null, 'Audio upload type must exist');
          assert(document.body.querySelector('[data-testid="upload-type-transcription"]') !== null, 'Transcription upload type must exist');
          assert(document.body.querySelector('[data-testid="upload-type-pdf"]') !== null, 'PDF upload type must exist');
          assert(document.body.querySelector('[data-testid="upload-type-video"]') !== null, 'Video upload type must exist');
          assert(document.body.querySelector('[data-testid="upload-type-cancel"]') !== null, 'Cancel button must exist');
        },
        200
      );
    },
  },

];
